import React, { useState, useCallback } from 'react';
import { useUpdateAtom } from 'jotai/utils';
import { chatMessagesAtom, chatSessionIdAtom, chatHistoryViewAtom, chatSessionOriginAtom, chatSessionScheduleIdAtom } from 'src/shared/chat';
import { useChatSessionsQuery } from 'src/api/queries/chatQueries';
import { useDeleteChatSessionMutation } from 'src/api/mutations/chatMutations';
import { getChatSessionDetail } from 'src/api/functions/chatApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import type { ChatSessionSummary, ChatSessionDetail } from './types';
import styles from './ChatHistory.module.scss';

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface ChatHistoryProps {
  onBack: () => void;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ onBack }) => {
  const setMessages = useUpdateAtom(chatMessagesAtom);
  const setSessionId = useUpdateAtom(chatSessionIdAtom);
  const setHistoryView = useUpdateAtom(chatHistoryViewAtom);
  const setSessionOrigin = useUpdateAtom(chatSessionOriginAtom);
  const setSessionScheduleId = useUpdateAtom(chatSessionScheduleIdAtom);
  const accessToken = useAccessToken();

  const { data, isLoading, isError } = useChatSessionsQuery({ staleTime: 30000 });
  const deleteMutation = useDeleteChatSessionMutation();

  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleSelectSession = useCallback(
    async (session: ChatSessionSummary) => {
      setLoadingSessionId(session.session_id);
      try {
        const detail = (await getChatSessionDetail(accessToken, session.session_id)) as ChatSessionDetail;
        if (detail.messages) {
          setMessages(detail.messages);
          setSessionId(detail.session_id);
          setSessionOrigin(detail.origin || null);
          setSessionScheduleId(detail.schedule_id || null);
          setHistoryView(false);
        }
      } catch {
        // Silently fail — user stays on history view
      } finally {
        setLoadingSessionId(null);
      }
    },
    [accessToken, setMessages, setSessionId, setHistoryView],
  );

  const handleDelete = useCallback(
    (sessionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (confirmDeleteId === sessionId) {
        deleteMutation.mutate(sessionId);
        setConfirmDeleteId(null);
      } else {
        setConfirmDeleteId(sessionId);
      }
    },
    [confirmDeleteId, deleteMutation],
  );

  const sessions = data?.sessions || [];

  return (
    <div className={styles.container}>
      <div className={styles.historyHeader}>
        <button className={styles.backBtn} onClick={onBack} aria-label="Back to chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Back to chat</span>
        </button>
        <div className={styles.historyTitle}>Chat History</div>
      </div>

      <div className={styles.sessionList}>
        {isLoading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Loading sessions...</span>
          </div>
        )}

        {isError && (
          <div className={styles.errorState}>
            <span>Failed to load chat history.</span>
          </div>
        )}

        {!isLoading && !isError && sessions.length === 0 && (
          <div className={styles.emptyState}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className={styles.emptyIcon}>
              <path
                d="M12 3C7.03 3 3 6.58 3 11c0 2.52 1.37 4.76 3.5 6.2V21l3.2-1.78c.73.18 1.5.28 2.3.28 4.97 0 9-3.58 9-8s-4.03-8-9-8z"
                stroke="var(--color-gray-300)"
                strokeWidth={1.5}
              />
            </svg>
            <div className={styles.emptyText}>No chat history yet</div>
            <div className={styles.emptySubtext}>Your conversations will appear here</div>
          </div>
        )}

        {sessions.map((session) => (
          <button
            key={session.session_id}
            className={styles.sessionItem}
            onClick={() => handleSelectSession(session)}
            disabled={loadingSessionId === session.session_id}
          >
            <div className={styles.sessionContent}>
              <div className={styles.sessionTitle}>{session.title}</div>
              <div className={styles.sessionPreview}>{session.preview}</div>
              <div className={styles.sessionMeta}>
                <span>{session.message_count} messages</span>
                {session.tool_calls > 0 && (
                  <>
                    <span className={styles.metaDot} />
                    <span>{session.tool_calls} tool{session.tool_calls !== 1 ? 's' : ''}</span>
                  </>
                )}
                <span className={styles.metaDot} />
                <span>{formatRelativeTime(session.last_activity)}</span>
              </div>
            </div>
            <button
              className={styles.deleteBtn}
              onClick={(e) => handleDelete(session.session_id, e)}
              title={confirmDeleteId === session.session_id ? 'Click again to confirm' : 'Delete session'}
              aria-label="Delete session"
            >
              {confirmDeleteId === session.session_id ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="var(--color-danger-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            {loadingSessionId === session.session_id && (
              <div className={styles.sessionLoading}>
                <div className={styles.spinner} />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
